import { useState, useEffect, useRef, useCallback } from 'react';

export const useParallax = ({
  enabled = false,
  textColorOutside = '',
  textColorInside = '',
}) => {
  const [scrollY, setScrollY] = useState(0);
  const [isTextInside, setIsTextInside] = useState(false);
  const heroRef = useRef(null);
  const contentRef = useRef(null);
  const UPWARD_MOVEMENT_LIMIT = -30;
  const PARALLAX_MAX_OFFSET = 300;
  const PARALLAX_SCROLL_MULTIPLIER = 0.45;

  const handleScroll = useCallback(() => {
    if (!enabled || !heroRef.current || !contentRef.current) return;

    const scrollPosition = window.scrollY;
    setScrollY(scrollPosition);

    const heroRect = heroRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();

    // Check if text content is overlapping with the background image
    const heroTop = heroRect.top + scrollPosition;
    const heroBottom = heroTop + heroRect.height;
    const contentTop = contentRect.top + scrollPosition;
    const contentBottom = contentTop + contentRect.height;

    // Determine if text is inside the image area based on scroll position
    const isInside = contentTop < heroBottom && contentBottom > heroTop;
    setIsTextInside(isInside);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [enabled, handleScroll]);

  const getImageTransform = useCallback(() => {
    if (!enabled) return {};

    // Image stays sticky/fixed - no transform needed
    return {};
  }, [enabled]);

  const getImageStyles = useCallback(() => {
    if (!enabled) return {};

    // Image stays in normal position
    return {};
  }, [enabled]);

  const getTextTransform = useCallback(() => {
    if (!enabled || !heroRef.current) return {};

    // Get hero element position
    const heroRect = heroRef.current.getBoundingClientRect();
    const heroTop = heroRect.top + scrollY;

    // Only apply parallax when hero is in viewport
    if (heroRect.bottom < 0 || heroRect.top > window.innerHeight) {
      return {};
    }

    // Text moves down significantly as you scroll
    const scrollDelta = scrollY - heroTop;
    const parallaxOffset = Math.max(
      UPWARD_MOVEMENT_LIMIT, // Limit upward movement to prevent text from disappearing
      Math.min(PARALLAX_MAX_OFFSET, scrollDelta * PARALLAX_SCROLL_MULTIPLIER) // Even more downward movement
    );

    return {
      transform: `translateY(${parallaxOffset}px)`,
      willChange: 'transform',
    };
  }, [enabled, scrollY]);

  const getTextColor = useCallback(() => {
    if (!enabled) return 'currentColor';

    if (isTextInside && textColorInside) {
      return textColorInside;
    }
    if (!isTextInside && textColorOutside) {
      return textColorOutside;
    }

    return 'currentColor';
  }, [enabled, isTextInside, textColorInside, textColorOutside]);

  return {
    heroRef,
    contentRef,
    scrollY,
    isTextInside,
    getImageTransform,
    getImageStyles,
    getTextTransform,
    getTextColor,
    enabled,
  };
};
